import React, { useEffect } from "react";
import { Alert, Card, Checkbox, Col, Collapse, Form, InputNumber, Row, Select, Spin } from "antd";
import { BUNDESLAENDER, PERIODS, STEUERKLASSEN, formatCents } from "./payrollOptions";
import usePayrollPreview from "./usePayrollPreview";
import "./Payroll.css";

import { useTranslation } from "react-i18next";
/**
 * Live Lohnsteuer calculation. The service provider only has to supply a gross salary and a
 * Steuerklasse; every other Lohnsteuerabzugsmerkmal falls back to the value stored on the
 * employee, and from there to the defaults that apply to most employments.
 *
 * The figures come from the backend, which runs the official BMF Programmablaufplan — the
 * calculation is deliberately not duplicated in the browser so there is one source of truth.
 *
 * @param {object} props
 * @param {object} [props.employee]   Employee whose stored payroll settings are the defaults.
 * @param {object} [props.initialValues] Prefills the form (e.g. the gross of a salary slip).
 * @param {Function} [props.onChange] Receives ({ values, payroll }) after each calculation.
 */
const LohnsteuerCalculator = ({ employee, initialValues = {}, onChange }) => {
  const { t } = useTranslation("dashboard");
  const [form] = Form.useForm();
  const { payroll, loading, inputError, request, fetchPreview } = usePayrollPreview(employee?._id);

  const stored = employee?.payroll || {};
  const defaults = {
    period: "month",
    brutto: employee?.workSchedule === "monthly" ? employee?.salary : undefined,
    steuerklasse: stored.steuerklasse,
    kinderfreibetraege: stored.kinderfreibetraege ?? 0,
    churchTax: stored.churchTax ?? false,
    bundesland: stored.bundesland,
    zusatzbeitrag: stored.zusatzbeitrag,
    children: stored.children ?? employee?.numberOfKids ?? 0,
    privatelyInsured: stored.privatelyInsured ?? false,
    privateKvPvCosts: stored.privateKvPvCosts ?? 0,
    pensionExempt: stored.pensionExempt ?? false,
    unemploymentExempt: stored.unemploymentExempt ?? false,
    einmalzahlung: 0,
    ...initialValues
  };

  useEffect(() => {
    fetchPreview(defaults);
  }, [employee?._id]);

  useEffect(() => {
    if (onChange) onChange({ values: form.getFieldsValue(true), payroll });
  }, [payroll]);

  const privatelyInsured = Form.useWatch("privatelyInsured", form);

  const advanced = (
    <Row gutter={[16, 0]}>
      <Col xs={24} md={8}>
        <Form.Item
          name="kinderfreibetraege"
          label={t("Kinderfreibeträge")}
          tooltip={t(
            "From ELStAM. Only in Steuerklassen I–IV. Lowers Solidaritätszuschlag and Kirchensteuer, not the Lohnsteuer."
          )}
        >
          <InputNumber min={0} max={20} step={0.5} style={{ width: "100%" }} />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item
          name="children"
          label={t("Children")}
          tooltip={t(
            "Any child removes the Pflegeversicherung surcharge for childless employees; from the second child under 25 the rate drops further."
          )}
        >
          <InputNumber min={0} max={20} style={{ width: "100%" }} />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item
          name="zusatzbeitrag"
          label={t("Zusatzbeitrag (%)")}
          tooltip={t(
            "The employee's own health insurer's supplementary rate. Leave empty for the 2026 average of 2.9 %."
          )}
        >
          <InputNumber min={0} max={10} step={0.1} style={{ width: "100%" }} placeholder="2.9" />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item
          name="einmalzahlung"
          label={t("One-off payment (€)")}
          tooltip={t(
            "Sonstiger Bezug, e.g. Weihnachtsgeld. Taxed separately under § 39b Abs. 3 EStG."
          )}
        >
          <InputNumber min={0} step={100} style={{ width: "100%" }} />
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item name="privatelyInsured" label=" " valuePropName="checked">
          <Checkbox>{t("Privately insured (PKV)")}</Checkbox>
        </Form.Item>
      </Col>
      {privatelyInsured ? (
        <Col xs={24} md={8}>
          <Form.Item
            name="privateKvPvCosts"
            label={t("PKV basic premium (€/month)")}
            tooltip={t("Basiskranken- und Pflege-Pflichtversicherung as reported in ELStAM.")}
          >
            <InputNumber min={0} step={10} style={{ width: "100%" }} />
          </Form.Item>
        </Col>
      ) : null}
      <Col xs={24} md={8}>
        <Form.Item name="pensionExempt" label=" " valuePropName="checked">
          <Checkbox>{t("Exempt from Rentenversicherung")}</Checkbox>
        </Form.Item>
      </Col>
      <Col xs={24} md={8}>
        <Form.Item name="unemploymentExempt" label=" " valuePropName="checked">
          <Checkbox>{t("Exempt from Arbeitslosenversicherung")}</Checkbox>
        </Form.Item>
      </Col>
    </Row>
  );

  return (
    <div className="LohnsteuerCalculator">
      <Form
        form={form}
        layout="vertical"
        initialValues={defaults}
        onValuesChange={(_, values) => request(values)}
      >
        <Row gutter={[16, 0]}>
          <Col xs={24} md={8}>
            <Form.Item
              name="brutto"
              label={t("Gross salary (€)")}
              rules={[{ required: true, message: t("Enter the gross salary") }]}
            >
              <InputNumber min={0} step={100} style={{ width: "100%" }} placeholder="3.500" />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="steuerklasse"
              label={t("Steuerklasse")}
              rules={[{ required: true, message: t("Choose a Steuerklasse") }]}
            >
              <Select
                options={STEUERKLASSEN.map(o => ({ ...o, label: t(o.label) }))}
                placeholder={t("I–VI")}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="period" label={t("Pay period")}>
              <Select options={PERIODS.map(o => ({ ...o, label: t(o.label) }))} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="bundesland" label={t("Bundesland")}>
              <Select options={BUNDESLAENDER} allowClear placeholder={t("Select")} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="churchTax" label=" " valuePropName="checked">
              <Checkbox>{t("Liable for Kirchensteuer")}</Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <Collapse
          ghost
          items={[
            { key: "advanced", label: t("More tax and insurance details"), children: advanced }
          ]}
        />
      </Form>

      {inputError ? <Alert type="error" showIcon message={inputError} /> : null}

      <Spin spinning={loading}>
        <PayrollBreakdown payroll={payroll} />
      </Spin>
    </div>
  );
};

const Line = ({ label, cents, sign = "", strong, muted }) => (
  <div className={`payroll-line${strong ? " payroll-line--strong" : ""}`}>
    <span
      className={muted ? "payroll-line__label payroll-line__label--muted" : "payroll-line__label"}
    >
      {label}
    </span>
    <span className="payroll-line__value">
      {sign}
      {formatCents(cents)}
    </span>
  </div>
);

/** Renders the deduction chain the way a German payslip lists it. */
export const PayrollBreakdown = ({ payroll }) => {
  const { t } = useTranslation("dashboard");
  if (!payroll) {
    return (
      <Alert
        type="info"
        showIcon
        message={t("Enter a gross salary and a Steuerklasse to see the Lohnsteuer.")}
      />
    );
  }
  const { tax, socialInsurance: si, gross } = payroll;

  return (
    <Row gutter={[16, 16]} className="payroll-breakdown">
      <Col xs={24} lg={12}>
        <Card size="small" title={t("Employee")}>
          <Line label={t("Basic pay")} cents={gross.laufenderBezug} />
          {gross.sonstigerBezug > 0 ? (
            <Line label={t("One-off payment")} cents={gross.sonstigerBezug} />
          ) : null}
          {gross.sachbezuege > 0 ? (
            <Line label={t("Benefit in kind")} cents={gross.sachbezuege} />
          ) : null}
          {gross.vermoegenswirksameLeistungen > 0 ? (
            <Line label={t("VWL")} cents={gross.vermoegenswirksameLeistungen} />
          ) : null}
          {gross.zuschlaege?.grossCents > 0 ? (
            <Line label={t("Shift surcharges (§ 3b)")} cents={gross.zuschlaege.grossCents} />
          ) : null}
          <Line label={t("Gross total")} cents={gross.total} strong />
          {gross.bavEntgeltumwandlung > 0 ? (
            <Line
              label={t("Deferred into a pension (BAV)")}
              cents={gross.bavEntgeltumwandlung}
              sign="− "
            />
          ) : null}
          {gross.zuschlaege?.taxFreeCents > 0 ? (
            <Line
              label={t("of which tax-free surcharges")}
              cents={gross.zuschlaege.taxFreeCents}
              muted
            />
          ) : null}
          <Line label={t("Taxable gross")} cents={gross.steuerpflichtig} muted />
          <Line label={t("Lohnsteuer")} cents={tax.lohnsteuer} sign="− " />
          <Line label={t("Solidaritätszuschlag")} cents={tax.solidaritaetszuschlag} sign="− " />
          {tax.kirchensteuer > 0 ? (
            <Line
              label={`${t("Kirchensteuer")} (${Math.round(tax.kirchensteuerRate * 100)} %)`}
              cents={tax.kirchensteuer}
              sign="− "
            />
          ) : null}
          <Line label={t("Krankenversicherung")} cents={si.employee.kv} sign="− " />
          <Line label={t("Pflegeversicherung")} cents={si.employee.pv} sign="− " />
          <Line label={t("Rentenversicherung")} cents={si.employee.rv} sign="− " />
          <Line label={t("Arbeitslosenversicherung")} cents={si.employee.av} sign="− " />
          <Line label={t("Net pay")} cents={payroll.netto} strong />
          {payroll.auszahlungsbetrag !== payroll.netto ? (
            <Line label={t("Amount transferred")} cents={payroll.auszahlungsbetrag} strong />
          ) : null}
        </Card>
      </Col>
      <Col xs={24} lg={12}>
        <Card size="small" title={t("Employer")}>
          <Line label={t("Krankenversicherung")} cents={si.employer.kv} />
          <Line label={t("Pflegeversicherung")} cents={si.employer.pv} />
          <Line label={t("Rentenversicherung")} cents={si.employer.rv} />
          <Line label={t("Arbeitslosenversicherung")} cents={si.employer.av} />
          <Line label={t("Insolvenzgeldumlage (U3)")} cents={si.employer.insolvenzgeldumlage} />
          {si.employer.umlageU1 > 0 ? (
            <Line label={t("Umlage U1")} cents={si.employer.umlageU1} />
          ) : null}
          {si.employer.umlageU2 > 0 ? (
            <Line label={t("Umlage U2")} cents={si.employer.umlageU2} />
          ) : null}
          <Line label={t("Total employer cost")} cents={payroll.employerCost} strong />
        </Card>
        {(payroll.warnings || []).map(warning => (
          <Alert key={warning} type="warning" showIcon message={warning} className="mt-2" />
        ))}
      </Col>
    </Row>
  );
};

export default LohnsteuerCalculator;
